import { Box, Typography, Grid, Card } from "@mui/material";
import { useState } from "react";
import { asPercentage } from "components/ui/formatters";
import { MaxDropLimits } from "helpers/types";
import { drawdownComparisonChartStyles } from "../../../../styles/DrawdownComparisonChart.styles";
import { DrawdownComparisonEditModal } from "./DrawdownComparisonEditModal";
import {
  getBarDirection,
  getBarOffset,
  getBarWidth,
  getBarWidthPercentage,
} from "./comparisonChartUtils";

interface DrawdownComparisonChartProps {
  stockDrop: number;
  oeaDrop: number;
  maxDrop: number;
  negativeLimit: number;
  onLimitsChange: (limits: MaxDropLimits) => void;
}

export const DrawdownComparisonChart = ({
  stockDrop,
  oeaDrop,
  maxDrop,
  negativeLimit,
  onLimitsChange,
}: DrawdownComparisonChartProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const negativeLimitMagnitude = Math.abs(negativeLimit);
  const hasMixedSigns =
    (stockDrop < 0 && oeaDrop > 0) || (stockDrop > 0 && oeaDrop < 0);

  const stockPercentage = getBarWidthPercentage(
    stockDrop,
    maxDrop,
    negativeLimitMagnitude,
  );
  const oeaPercentage = getBarWidthPercentage(
    oeaDrop,
    maxDrop,
    negativeLimitMagnitude,
  );

  const stockFilledWidth = getBarWidth(stockPercentage, hasMixedSigns);
  const oeaFilledWidth = getBarWidth(oeaPercentage, hasMixedSigns);

  const stockBarDirection = getBarDirection(stockDrop);
  const oeaBarDirection = getBarDirection(oeaDrop);

  const rows = [
    {
      key: "stock",
      label: "Stock",
      value: stockDrop,
      width: stockFilledWidth,
      direction: stockBarDirection,
      filledStyle: drawdownComparisonChartStyles.barStockFilled,
      valueStyle: drawdownComparisonChartStyles.barStockValue,
      rowSx: drawdownComparisonChartStyles.barRow,
    },
    {
      key: "oea",
      label: "OEA",
      value: oeaDrop,
      width: oeaFilledWidth,
      direction: oeaBarDirection,
      filledStyle: drawdownComparisonChartStyles.barOeaFilled,
      valueStyle: drawdownComparisonChartStyles.barOeaValue,
      rowSx: {
        ...drawdownComparisonChartStyles.barRow,
        marginBottom: 0,
      },
    },
  ] as const;

  return (
    <>
      <Card
        sx={drawdownComparisonChartStyles.card}
        onClick={openModal}
        tabIndex={0}
        role="button"
        aria-label="Click to edit drawdown limit"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openModal();
          }
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs="auto">
            <Box sx={drawdownComparisonChartStyles.titleContainer}>
              <Typography sx={drawdownComparisonChartStyles.titleLine1}>
                Drawdown
              </Typography>
              <Typography sx={drawdownComparisonChartStyles.titleLine2}>
                Comparison
              </Typography>
            </Box>
          </Grid>
          <Grid item xs>
            <Box sx={drawdownComparisonChartStyles.barsContainer}>
              {rows.map((row) => (
                <Box key={row.key} sx={row.rowSx}>
                  <Typography sx={drawdownComparisonChartStyles.barLabel}>
                    {row.label}
                  </Typography>
                  <Box sx={drawdownComparisonChartStyles.barContainer}>
                    <Box
                      sx={{
                        ...row.filledStyle,
                        width: row.width,
                        left:
                          row.direction === "left"
                            ? getBarOffset(hasMixedSigns)
                            : "auto",
                        right:
                          row.direction === "right"
                            ? getBarOffset(hasMixedSigns)
                            : "auto",
                      }}
                    />
                  </Box>
                  <Typography sx={row.valueStyle}>
                    {asPercentage(row.value)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Card>

      <DrawdownComparisonEditModal
        isOpened={isModalOpen}
        onClose={closeModal}
        maxDrop={maxDrop}
        negativeLimit={negativeLimit}
        onLimitsChange={onLimitsChange}
      />
    </>
  );
};
